import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

import Stack from 'components/shared/Stack';

import { isBrowserSupportedMediaType } from 'utils/functions';
import { CommunicationDisplay, Colors, Message, SmsMedia } from 'typings';

import { ReactComponent as SmsIcon } from 'assets/images/inbound-sms.svg';

import CopyMessage from './CopyMessage';
import { Mms as MmsIcon } from '@material-ui/icons';
import Loader from 'components/Loader';
import { useDispatch } from 'react-redux';
import { apiFetchMessageMedia } from 'state/lead.state';

interface SmsProps extends CommunicationDisplay {
  data: Message;
  children: React.ReactNode;
  caption?: React.ReactElement;
}

const useStyles = makeStyles((theme: Theme) => ({
  messageContent: (props: SmsProps) => ({
    alignItems: props.data.incoming ? 'flex-start' : 'flex-end',
    width: '100%',
  }),
  text: (props: SmsProps) => ({
    position: 'relative',
    display: 'inline-flex',
    flexDirection: 'column',
    minWidth: 140,
    backgroundColor: props.data.incoming
      ? theme.palette.background.custom
      : theme.palette.action.selected,
    boxShadow: `0px 3px 6px ${Colors.TRANSPARENT_BLACK}`,
    borderRight: props.data.incoming ? `5px solid ${theme.palette.secondary.main}` : 'inherit',
    borderLeft: !props.data.incoming ? `5px solid ${theme.palette.secondary.main}` : 'inherit',
    borderRadius: theme.spacing(0.75),
    padding: props.data.incoming ? theme.spacing(1, 1, 1, 3) : theme.spacing(1, 3, 1, 1),
  }),
  caption: (props: SmsProps) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 500,
    padding: theme.spacing(0.5, 1),
    color: theme.palette.grey['500'],
    textAlign: props.data.incoming ? 'right' : 'inherit',
  }),
  svg: (props: SmsProps) => ({
    position: 'absolute',
    top: 'calc(50% - 1rem)',
    left: props.data.incoming ? theme.spacing(-2) : 'auto',
    right: !props.data.incoming ? theme.spacing(-2) : 'auto',
    width: theme.spacing(4),
    borderRadius: '50%',
    '& circle': {
      fill: theme.palette.secondary.main,
    },
  }),
  message: {
    whiteSpace: 'pre-wrap',
  },
  issue: (props: SmsProps) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 'var(--s-2)',
    textDecoration: props.data.isFlagged ? 'none' : 'underline',
    color: props.data.isFlagged ? 'red' : 'inherit',
    textTransform: 'none',
    height: theme.spacing(1),
    padding: 0,
    '&:hover': {
      backgroundColor: 'inherit',
    },
    '& svg': {
      display: 'inline-flex',
      width: theme.spacing(1.5),
    },
  }),
  messageError: {
    color: Colors.RED,
    fontWeight: 'bold',
    margin: theme.spacing(0, 1),
  },
  mediaContainer: (props: SmsProps) => ({
    alignItems: props.data.incoming ? 'flex-start' : 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& > img': {
      maxWidth: '50%',
    },
  }),
  unloadedMediaContainer: {
    alignItems: 'center',
    border: `1px solid ${theme.palette.grey['300']}`,
    borderRadius: theme.spacing(0.75),
    color: theme.palette.grey['500'],
    display: 'flex',
    justifyContent: 'center',
    minHeight: 60,
    overflow: 'hidden',
    textTransform: 'none',
    width: 200,
  },
}));

const MMS = (props: SmsMedia) => {
  if (!isBrowserSupportedMediaType(props.mimeType)) {
    return (
      <p>
        Your browser does not support this media type. Here is a{' '}
        <a href={props.url} target="_blank" rel="noopener noreferrer">
          link
        </a>{' '}
        to the file.
      </p>
    );
  }

  const mediaType = props.mimeType.match(/^(image|audio|video)/);
  let component = null;

  if (!mediaType) return component;

  switch (mediaType[0]) {
    case 'image':
      component = (
        <img
          src={props.data ? `data:${props.mimeType};base64,${props.data}` : props.url}
          alt="Sms File"
        />
      );
      break;
    case 'audio':
      component = (
        <audio controls>
          <source
            src={props.data ? `data:${props.mimeType};base64,${props.data}` : props.url}
            type={props.mimeType}
          ></source>
        </audio>
      );
      break;
    case 'video':
      component = (
        <video width="320" height="240" controls>
          <source
            src={props.data ? `data:${props.mimeType};base64,${props.data}` : props.url}
            type={props.mimeType}
          ></source>
        </video>
      );
      break;
    default:
      break;
  }

  return component;
};

const Sms = ({ data, children, caption, isRevealed }: React.PropsWithChildren<SmsProps>) => {
  const dispatch = useDispatch();
  const classes = useStyles({ data, children, isRevealed });
  const [inHover, setHover] = useState(false);
  const [isLoadingMedia, setLoadingMedia] = useState(false);

  const showMedia = data.media && (isRevealed || !data.hasIssue);
  const loadedMediaCount = data.media?.length ?? 0;
  const unloadedMediaCount = data.mediaCount - loadedMediaCount;
  const showCopyButton = data.incoming && inHover && !data.hasIssue && data.text;

  // when the media is loaded, set the loading state to false
  useEffect(() => {
    if (loadedMediaCount) {
      setLoadingMedia(false);
    }
  }, [loadedMediaCount]);

  const toggleHover = () => {
    setHover(!inHover);
  };

  const loadMedia = () => {
    setLoadingMedia(true);
    dispatch(apiFetchMessageMedia(data.id));
  };

  return (
    <>
      <Stack className={classes.messageContent}>
        {/* MMS have no text. Empty sms content will not be rendered. */}
        {!!children && (
          <div className={classes.text} onMouseEnter={toggleHover} onMouseLeave={toggleHover}>
            <SmsIcon className={classes.svg} />
            <Typography className={classes.message} variant="body2">
              {children}
            </Typography>
            {!!showCopyButton && <CopyMessage text={data.text} />}
          </div>
        )}
        {!!showMedia && (
          <>
            {data.media.map((media) => (
              <div key={media.url} className={classes.mediaContainer}>
                <MMS {...media} />
              </div>
            ))}
            {!!unloadedMediaCount && (
              <Button className={classes.unloadedMediaContainer} onClick={loadMedia}>
                {isLoadingMedia && <Loader />}
                <MmsIcon />
                &nbsp; Load {unloadedMediaCount} media
              </Button>
            )}
          </>
        )}
      </Stack>
      {caption}
    </>
  );
};

export default Sms;
